import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  TextField,
  Typography,
  Paper,
  alpha,
  Alert,
  Stack,
  useTheme,
  IconButton,
  InputAdornment,
  Divider
} from "@mui/material";
import axios from "axios";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Facebook as FacebookIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  MusicNote as MusicNoteIcon,
  Link as LinkIcon,
  Dashboard as DashboardIcon
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { authJsonHeaders, siteCmsUrls } from "../../api/siteCms";

const SocialMediaEdit = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  
  // Modified to match color scheme in SocialMediaView
  const baseColors = colors;
  const enhancedColors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
  };

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  // Social media platform configurations
  const socialPlatforms = [
    {
      id: "facebook",
      name: "Facebook",
      icon: <FacebookIcon />,
      color: "#1877F2",
      fieldName: "fbLink"
    },
    {
      id: "instagram",
      name: "Instagram",
      icon: <InstagramIcon />,
      color: "#E4405F",
      fieldName: "instagramLink"
    },
    {
      id: "tikTok",
      name: "TikTok",
      icon: <MusicNoteIcon />,
      color: "#000000",
      fieldName: "tikTokLink"
    },
    {
      id: "youtube",
      name: "YouTube",
      icon: <YouTubeIcon />,
      color: "#FF0000",
      fieldName: "youtubeLink"
    }
  ];

  const validationSchema = Yup.object({
    facebook: Yup.string()
      .url("Invalid URL format")
      .required("Facebook link is required"),
    instagram: Yup.string()
      .url("Invalid URL format")
      .required("Instagram link is required"),
    tikTok: Yup.string()
      .url("Invalid URL format")
      .required("TikTok link is required"),
    youtube: Yup.string()
      .url("Invalid URL format")
      .required("YouTube link is required"),
  });

  const formik = useFormik({
    initialValues: {
      facebook: "",
      instagram: "",
      tikTok: "",
      youtube: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        const payload = {
          fbLink: values.facebook,
          instagramLink: values.instagram,
          tikTokLink: values.tikTok,
          youtubeLink: values.youtube
        };
        
        await axios.put(siteCmsUrls.socialLinks, payload, {
          headers: authJsonHeaders(),
        });
        setSeverity("success");
        setSnackbarMessage("Social media links updated successfully");
        setSnackbarOpen(true);
        setTimeout(() => {
          navigate("/social");
        }, 2000);
      } catch (err) {
        setError(err);
        setSeverity("error");
        setSnackbarMessage("Error updating social media links");
        setSnackbarOpen(true);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchSocialMediaLinks = async () => {
      try {
        const response = await axios.get(siteCmsUrls.socialLinks);
        if (response.data.status) {
          formik.setValues({
            facebook: response.data.data.fbLink || "",
            instagram: response.data.data.instagramLink || "",
            tikTok: response.data.data.tikTokLink || "",
            youtube: response.data.data.youtubeLink || "",
          });
        } else {
          throw new Error(response.data.message);
        }
      } catch (err) {
        setError(err.message);
        setSeverity("error");
        setSnackbarMessage("Error fetching social media links");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSocialMediaLinks();
  }, []);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleBackClick = () => {
    navigate("/social");
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px" height="80vh" overflow="auto" paddingRight="20px">
      {/* Header Section - Using the gradient style from SocialMediaView */}
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, ${enhancedColors.blueAccent[500]} 0%, ${enhancedColors.blueAccent[400]} 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <DashboardIcon sx={{ color: "white", fontSize: 28 }} />
              <Typography variant="h3" fontWeight="bold" color="white">
                Edit Social Media Links
              </Typography>
            </Box>
            <Typography variant="body1" color="white">
              Update your social media presence and connections
            </Typography>
          </Box>
          <IconButton
            onClick={handleBackClick}
            sx={{
              backgroundColor: alpha("#ffffff", 0.2),
              color: "white",
              "&:hover": { backgroundColor: alpha("#ffffff", 0.3) },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Stack>
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: 3,
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
          borderRadius: 2,
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {socialPlatforms.map((platform) => (
              <Grid item xs={12} key={platform.id}>
                <TextField
                  fullWidth
                  id={platform.id}
                  name={platform.id}
                  placeholder={`Enter ${platform.name} URL`}
                  value={formik.values[platform.id]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched[platform.id] && Boolean(formik.errors[platform.id])}
                  helperText={formik.touched[platform.id] && formik.errors[platform.id]}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box 
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: alpha(enhancedColors.blueAccent[500], 0.2),
                              borderRadius: "8px",
                              p: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 1
                            }}
                          >
                            {React.cloneElement(platform.icon, {
                              sx: {
                                color: enhancedColors.blueAccent[500],
                                fontSize: 24,
                              },
                            })}
                          </Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="#333333"
                          >
                            {platform.name}
                          </Typography>
                        </Box>
                      </InputAdornment>
                    ),
                    endAdornment: formik.values[platform.id] && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => window.open(formik.values[platform.id], '_blank')}
                          sx={{ 
                            color: enhancedColors.blueAccent[500],
                            '&:hover': { 
                              backgroundColor: alpha(enhancedColors.blueAccent[500], 0.1) 
                            }
                          }}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 2,
                      borderColor: "#e0e0e0",
                      padding: "4px 4px 4px 12px",
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: enhancedColors.blueAccent[400],
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: enhancedColors.blueAccent[500],
                      }
                    },
                  }}
                />
              </Grid>
            ))}

            <Grid item xs={12} mt={2}>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={handleBackClick}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    borderColor: "#e0e0e0",
                    color: "#666666",
                    "&:hover": {
                      borderColor: "#999999",
                      backgroundColor: "#f8f8f8",
                    },
                    padding: "10px 16px",
                  }}
                >
                  Cancel
                </Button>
                
                <Button
                  variant="contained"
                  type="submit"
                  disabled={submitting || !formik.isValid}
                  startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                  sx={{
                    backgroundColor: enhancedColors.blueAccent[500],
                    color: "white",
                    "&:hover": { backgroundColor: enhancedColors.blueAccent[600] },
                    "&.Mui-disabled": {
                      backgroundColor: alpha(enhancedColors.blueAccent[500], 0.5),
                    },
                    padding: "10px 20px",
                    fontSize: "14px",
                  }}
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SocialMediaEdit;